/**
 * Ledger accounts endpoints (bos-api, BOS-91).
 *
 * Paths are relative to the axios client's base, which already ends in
 * `/api/v1` — so `/ledger/accounts` reaches `…/api/v1/ledger/accounts`. This
 * mirrors `SettingsEndpoints`, the other live ledger resource.
 *
 * `DELETE /accounts/{id}` exists on the API and is **deliberately absent here**.
 * Accounts are archived, never deleted, so that history stays intact and
 * auditable. The server's guard (childless, no postings) is not sufficient
 * protection: an account can satisfy it and still be referenced by a statement
 * mapping, a VAT role, or a fixed-asset / payroll account mapping, and deleting
 * it breaks those silently. Not having the URL here is the cheapest way to keep
 * it unreachable. See `CONTRACT.md` §"Hard delete".
 */

/** Ids are server-shaped, but escaping keeps the path well-formed. */
const seg = (value: string): string => encodeURIComponent(value);

/** Query options shared by the balance-bearing reads. */
export type AccountListQuery = {
    /** Which locale the server should resolve `name` into. */
    locale?: string;
    /** Include archived accounts. Defaults to the server's `false`. */
    includeArchived?: boolean;
    /**
     * Historical balances as of a `yyyy-MM-dd` date. Not used by the screen
     * today; carried because it is the mechanism when history lands.
     */
    asOf?: string;
};

const query = ({ locale, includeArchived, asOf }: AccountListQuery): string => {
    const params = new URLSearchParams();
    if (locale) {
        params.set('locale', locale);
    }
    if (includeArchived !== undefined) {
        params.set('includeArchived', String(includeArchived));
    }
    if (asOf) {
        params.set('asOf', asOf);
    }
    const search = params.toString();
    return search ? `?${search}` : '';
};

export const AccountsEndpoints = {
    /** GET — the full chart (unpaginated), each row with its own balance. */
    list: (options: AccountListQuery = {}) =>
        `/ledger/accounts${query(options)}`,

    /**
     * GET — one account with the **full locale map** for its name. The edit
     * drawer needs this; a list row only carries the resolved string.
     */
    byId: (id: string) => `/ledger/accounts/${seg(id)}`,

    /**
     * GET — the server-nested tree. Not used: the chart is nested client-side
     * so siblings can be ordered by code (the server orders by name, which is
     * localized and therefore reorders when the language is switched). Kept so
     * the path is recorded in one place rather than rediscovered.
     */
    tree: (options: AccountListQuery = {}) =>
        `/ledger/accounts/tree${query(options)}`,

    /** POST — create. Idempotent on the client-supplied id; 200 or 201. */
    create: () => '/ledger/accounts',

    /** PUT — patch-style update. Requires the last-seen etag. */
    update: (id: string) => `/ledger/accounts/${seg(id)}`,

    /** POST — archive. 200 with the updated account and a fresh etag. */
    archive: (id: string) => `/ledger/accounts/${seg(id)}/archive`,

    /** POST — reactivate. 200 with the updated account and a fresh etag. */
    reactivate: (id: string) => `/ledger/accounts/${seg(id)}/reactivate`,
};
